import React from 'react';
import ReactDOM from 'react-dom/client';
import { hydrateRoot } from 'react-dom/client';
import './index.css';
import App from './App';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { HelmetProvider } from 'react-helmet-async';
import mixpanel from 'mixpanel-browser';
import './i18n/config';
import { getUTMParams, parseUTMParams, storeUTMParams } from './utils/utmParams';

// Initialize Mixpanel with your project token
const mixpanelToken = process.env.REACT_APP_MIXPANEL_TOKEN || '9301ea9f0799438a00c9ee3ead0c9947';

if (mixpanelToken) {
  mixpanel.init(mixpanelToken, {
    api_host: 'https://api-eu.mixpanel.com',
    debug: true, // Enables debug mode
    track_pageview: true, // Automatically tracks page views
    persistence: 'localStorage', // Stores data in localStorage
  });

  // Verify Mixpanel setup
  const verifyMixpanelSetup = () => {
    const isInitialized = mixpanel.has_opted_in_tracking() !== undefined || 
                         mixpanel.get_distinct_id() !== undefined;
    
    const tokenSource = process.env.REACT_APP_MIXPANEL_TOKEN ? 'environment variable' : 'fallback (hardcoded)';
    
    console.log('🔍 Mixpanel Setup Verification:');
    console.log('  ✓ Initialized:', isInitialized);
    console.log('  ✓ Token source:', tokenSource);
    console.log('  ✓ Token:', mixpanelToken.substring(0, 8) + '...');
    console.log('  ✓ Distinct ID:', mixpanel.get_distinct_id());
    console.log('  ✓ Debug mode:', true);
    console.log('  ✓ Check Network tab for requests to api-eu.mixpanel.com');
    console.log('  ✓ Check localStorage for keys starting with "mp_"');
    
    if (!process.env.REACT_APP_MIXPANEL_TOKEN) {
      console.warn('⚠️  Using fallback token. Set REACT_APP_MIXPANEL_TOKEN in your .env file.');
    }
    
    if (!isInitialized) {
      console.warn('⚠️  Mixpanel may not be properly initialized');
    }
  };

  // Run verification after a short delay to ensure initialization is complete
  setTimeout(verifyMixpanelSetup, 100);

  // Handle UTM parameters from marketing campaigns (e.g., Meta ads)
  const urlUTMParams = parseUTMParams();
  const allUTMParams = getUTMParams(); // Gets URL params if present, otherwise stored params
  
  if (Object.keys(urlUTMParams).length > 0) {
    // New UTM params detected in URL - store them
    storeUTMParams(urlUTMParams);
    console.log('📊 UTM Parameters detected:', urlUTMParams);
    
    // Register UTM params as user properties for attribution
    const userProperties: Record<string, string | undefined> = {
      utm_source: urlUTMParams.utm_source,
      utm_medium: urlUTMParams.utm_medium,
      utm_campaign: urlUTMParams.utm_campaign,
    };
    
    // Add optional UTM params if present
    if (urlUTMParams.utm_term) userProperties.utm_term = urlUTMParams.utm_term;
    if (urlUTMParams.utm_content) userProperties.utm_content = urlUTMParams.utm_content;
    
    // Set first-touch attribution (only if not already set)
    // Use $set_once to preserve first touch attribution
    mixpanel.people.set_once({
      first_utm_source: urlUTMParams.utm_source,
      first_utm_medium: urlUTMParams.utm_medium,
      first_utm_campaign: urlUTMParams.utm_campaign,
    });
    
    // Update current/last touch attribution
    mixpanel.people.set(userProperties);
  } else if (Object.keys(allUTMParams).length > 0) {
    // Using stored UTM params (user returned without new UTM params)
    console.log('📊 Using stored UTM Parameters:', allUTMParams);
  }

  // Track the initial page load with UTM parameters
  mixpanel.track("Page Loaded", {
    ...allUTMParams,
    has_utm_params: Object.keys(allUTMParams).length > 0,
  });
} else {
  console.warn('⚠️  Mixpanel token not found. Analytics will not be tracked.');
}


const rootElement = document.getElementById('root') as HTMLElement;

// Application JSX content
const appJsx = (
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);

// Check if the app has been prerendered
if (rootElement.hasChildNodes()) {
  // Use hydrateRoot for React 18 with prerendered content
  hydrateRoot(rootElement, appJsx);
} else {
  // Use createRoot for initial client render
  const root = ReactDOM.createRoot(rootElement);
  root.render(appJsx);
}
